import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import { SiteHeader, PageFooter } from "@/components/brand/Brand";
import { ConceptDiagram, FlowStrip, ModuleCard, MODULE_ICONS as I } from "@/components/home/HomeSections";
import { KpiNumber } from "@/components/common/KpiNumber";
import { useDemo } from "@/lib/demo/store";
import { buildDashboardView, companyStats, isInside } from "@/lib/demo/analytics";
import { fmt } from "@/lib/demo/utils";
import { useI18n } from "@/lib/i18n/i18n";

const STORY = [
  ["María se registra", "/registro"], ["Recibe su QR", "/credencial"], ["Entra en el Foro", "/acceso"],
  ["Visita Bosch", "/empresa/scanner"], ["Bosch escanea su QR", "/empresa/scanner"], ["CV compartido automáticamente", "/empresa/scanner"],
  ["María aparece en el portal Bosch", "/empresa/portal?s=candidatos"], ["UPV ve la interacción en su dashboard", "/dashboard"],
];

export default function Home() {
  const { state, activeParticipant: p } = useDemo();
  const dash = useMemo(() => buildDashboardView("live", state), [state]);
  const bosch = useMemo(() => companyStats(state, "bosch"), [state]);
  const inside = isInside(state, p.id);
  const { t } = useI18n();

  const modules = [
    { to: "/registro", icon: I.UserPlus, title: t("Registro participante"), desc: t("Inscripción previa con datos académicos, CV en PDF y consentimiento claro."), live: t("{n} registrados", { n: fmt(dash.kpis.registrados) }) },
    { to: "/credencial", icon: I.QrCode, title: t("Credencial digital"), desc: t("Un QR único y seguro. Sin datos personales en el código."), live: p.token },
    { to: "/acceso", icon: I.DoorOpen, title: t("Control de accesos"), desc: t("Entradas y salidas ilimitadas con lectura en un segundo."), live: t(inside ? "{name}: dentro del recinto" : "{name}: fuera del recinto", { name: p.firstName }) },
    { to: "/empresa/scanner", icon: I.ScanLine, title: t("Scanner empresa"), desc: t("Escanear → recibir CV → siguiente. Sin formularios ni confirmaciones."), live: t("Bosch · {n} candidatos hoy", { n: fmt(bosch.today) }), featured: true },
    { to: "/empresa/portal", icon: I.Building2, title: t("Portal empresa"), desc: t("Candidatos recibidos, perfiles, CV y exportación a Excel."), live: t("{n} candidatos Bosch", { n: fmt(bosch.total) }) },
    { to: "/dashboard", icon: I.BarChart3, title: t("Dashboard UPV"), desc: t("Afluencia, ocupación en directo e interacción con empresas."), live: t("{n} CV compartidos", { n: fmt(dash.kpis.cv) }) },
  ];

  return (
    <div className="min-h-screen bg-white pb-28" data-testid="home-page">
      <SiteHeader right={<span className="rounded-full bg-upv-light px-3 py-1 text-[11px] font-semibold text-upv">{t("Demo comercial")}</span>} />

      <section className="mx-auto grid max-w-7xl gap-12 px-5 pb-12 pt-14 lg:grid-cols-12 lg:px-10 lg:pt-20">
        <div className="lg:col-span-6 lg:pt-6">
          <p className="fade-up text-xs font-bold uppercase tracking-[0.25em] text-upv">{t("Foro de Empleo UPV 2027 · 12–13 mayo")}</p>
          <h1 className="fade-up-1 mt-5 font-display text-4xl font-bold leading-[0.95] tracking-tight text-slate-950 sm:text-5xl lg:text-6xl" data-testid="home-title">
            UPV Career Fair Platform
          </h1>
          <p className="fade-up-2 mt-6 font-display text-2xl font-semibold text-upv sm:text-3xl" data-testid="home-subtitle">{t("Una identidad. Todo el evento.")}</p>
          <p className="fade-up-2 mt-4 max-w-lg text-base leading-relaxed text-slate-600">
            {t("Acceso, interacción con empresas y currículums conectados mediante una única credencial digital.")}
          </p>
          <div className="fade-up-3 mt-9 flex flex-wrap gap-3">
            <Link to="/registro" className="inline-flex items-center gap-2 rounded-full bg-upv px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-upv-dark" data-testid="home-start-demo-button">
              <Play className="h-4 w-4" /> {t("Iniciar la demo")}
            </Link>
            <Link to="/credencial" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3.5 text-sm font-semibold text-slate-800 transition-colors hover:border-slate-900" data-testid="home-view-credential-button">
              {t("Ver credencial de {name}", { name: p.firstName })} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="fade-up-3 mt-12 grid max-w-lg grid-cols-3 gap-6 border-t border-slate-200 pt-6">
            {[["Registrados", dash.kpis.registrados, "home-kpi-registrados"], ["En el recinto", dash.kpis.inside, "home-kpi-inside"], ["CV compartidos", dash.kpis.cv, "home-kpi-cv"]].map(([l, v, tid]) => (
              <div key={l}>
                <KpiNumber value={v} testId={tid} className="font-display text-2xl font-bold text-slate-950 sm:text-3xl" />
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{t(l)}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="fade-up-2 lg:col-span-6"><ConceptDiagram participant={p} /></div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="flex flex-col gap-4 border-y border-slate-200 py-6 lg:flex-row lg:items-center lg:justify-between">
          <p className="font-display text-lg font-bold tracking-tight">{t("UN QR. TODA LA EXPERIENCIA.")}</p>
          <FlowStrip />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-10">
        <div className="mb-10 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-400">{t("Módulos conectados")}</p>
            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("Seis piezas, un mismo estado.")}</h2>
          </div>
          <p className="max-w-sm text-sm text-slate-500">{t("Cada lectura en un módulo se refleja al instante en todos los demás.")}</p>
        </div>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {modules.map((m, i) => <ModuleCard key={m.to} n={i + 1} {...m} testId={`module-card-${i + 1}`} />)}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="rounded-[32px] bg-slate-50 p-8 sm:p-12" data-testid="demo-script">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-upv">{t("Guion de la demo · 5 minutos")}</p>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight">{t("La historia de {name}", { name: p.firstName })}</h2>
          <ol className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {STORY.map(([label, to], i) => (
              <li key={label}>
                <Link to={to} className="group flex h-full items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition-colors hover:border-upv" data-testid={`demo-script-step-${i + 1}`}>
                  <span className="font-mono text-xs font-bold text-upv">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm font-semibold text-slate-800">{t(label)}</span>
                </Link>
              </li>
            ))}
          </ol>
        </div>
      </section>
      <PageFooter />
    </div>
  );
}
